import { Router } from '@angular/router';
import { pageUrl } from './constants';

export const PRINT_PREVIEW_READY_MESSAGE_TYPE = 'pactrak-print-preview-ready';

/**
 * Max time the handshake stays open. The preview window boots the full app
 * (bundle + MSAL guard), which can exceed a few seconds on slow loads.
 */
export const PRINT_PREVIEW_HANDSHAKE_TIMEOUT_MS = 60000;
const PRINT_PREVIEW_READY_PING_MS = 300;
const PRINT_PREVIEW_CLOSED_POLL_MS = 1000;

export interface PrintPreviewReadyMessage {
  type: typeof PRINT_PREVIEW_READY_MESSAGE_TYPE;
}

export function isPrintPreviewReadyMessage(event: MessageEvent): boolean {
  const data = event.data as PrintPreviewReadyMessage | null;
  return data?.type === PRINT_PREVIEW_READY_MESSAGE_TYPE;
}

/**
 * Child print window: true only for the real print payload from the opener.
 * Ignores same-origin noise (dev-server/HMR strings, browser-extension or
 * devtools messages, null data) that previously stopped the ready ping and
 * left the preview blank.
 */
export function isPrintPayloadMessage(
  event: MessageEvent,
  requiredKeys: readonly string[]
): boolean {
  if (event.origin !== globalThis.window.location.origin) {
    return false;
  }
  const opener = globalThis.window.opener;
  if (opener && event.source !== opener) {
    return false;
  }
  const data = event.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }
  if (isPrintPreviewReadyMessage(event)) {
    return false;
  }
  return requiredKeys.some(key => key in data);
}

/** Child print window: signal opener until print payload arrives or timeout. */
export function startPrintPreviewReadyNotifier(
  isPayloadReceived: () => boolean
): () => void {
  const opener = globalThis.window?.opener;
  if (!opener || opener.closed) {
    return () => undefined;
  }

  const origin = globalThis.window.location.origin;
  const postReady = (): void => {
    if (isPayloadReceived()) {
      return;
    }
    opener.postMessage(
      {
        type: PRINT_PREVIEW_READY_MESSAGE_TYPE,
      } satisfies PrintPreviewReadyMessage,
      origin
    );
  };

  postReady();
  const intervalId = globalThis.setInterval(
    postReady,
    PRINT_PREVIEW_READY_PING_MS
  );
  const timeoutId = globalThis.setTimeout(() => {
    globalThis.clearInterval(intervalId);
  }, PRINT_PREVIEW_HANDSHAKE_TIMEOUT_MS);

  return () => {
    globalThis.clearInterval(intervalId);
    globalThis.clearTimeout(timeoutId);
  };
}

export interface PostPrintDataWhenReadyOptions {
  timeoutMs?: number;
}

/**
 * Pending sender per preview window. All previews share the window name
 * 'Child Window', so a second Preview click reuses the same window; the
 * previous sender must be cancelled or it would post stale data first.
 */
const pendingPrintSenders = new WeakMap<Window, () => void>();

/** Parent window: wait for child ready ping, then post print payload once. */
export function postPrintDataWhenReady(
  childWindow: Window | null,
  data: unknown,
  options?: PostPrintDataWhenReadyOptions
): void {
  if (!childWindow) {
    return;
  }

  pendingPrintSenders.get(childWindow)?.();

  const origin = globalThis.window.location.origin;
  const timeoutMs = options?.timeoutMs ?? PRINT_PREVIEW_HANDSHAKE_TIMEOUT_MS;
  let sent = false;

  const cleanup = (): void => {
    globalThis.window.removeEventListener('message', onReadyMessage);
    globalThis.clearTimeout(timeoutId);
    globalThis.clearInterval(closedPollId);
    if (pendingPrintSenders.get(childWindow) === cleanup) {
      pendingPrintSenders.delete(childWindow);
    }
  };

  const sendData = (): void => {
    if (sent || childWindow.closed) {
      cleanup();
      return;
    }
    childWindow.postMessage(data, origin);
    sent = true;
    cleanup();
  };

  const onReadyMessage = (event: MessageEvent): void => {
    if (event.source !== childWindow || event.origin !== origin) {
      return;
    }
    if (isPrintPreviewReadyMessage(event)) {
      sendData();
    }
  };

  globalThis.window.addEventListener('message', onReadyMessage);

  const timeoutId = globalThis.setTimeout(() => cleanup(), timeoutMs);
  // Stop listening as soon as the user closes the preview window.
  const closedPollId = globalThis.setInterval(() => {
    if (childWindow.closed) {
      cleanup();
    }
  }, PRINT_PREVIEW_CLOSED_POLL_MS);
  pendingPrintSenders.set(childWindow, cleanup);
}

export function normalizeAppPath(path: string): string {
  const withoutQuery = path.split('?')[0].split('#')[0];
  return withoutQuery.endsWith('/') && withoutQuery.length > 1
    ? withoutQuery.slice(0, -1)
    : withoutQuery;
}

export function isPrintPreviewPath(path: string): boolean {
  return (
    path === pageUrl.printPreviewTable ||
    path === pageUrl.printPreviewInfoTable ||
    path === pageUrl.generateShipmentLabel
  );
}

/** True when header/sidebar/breadcrumb should be hidden (print popup or label preview). */
export function shouldHideAppChrome(router: Router): boolean {
  if (typeof globalThis.window !== 'undefined') {
    const browserPath = normalizeAppPath(globalThis.window.location.pathname);
    if (isPrintPreviewPath(browserPath)) {
      return true;
    }
  }

  const routerPath = normalizeAppPath(router.url);
  if (isPrintPreviewPath(routerPath)) {
    return true;
  }

  let route = router.routerState.snapshot.root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route.data['hideLayout'] === true;
}
